import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useActiveWorkspace } from '@/state/selection.store'
import { Input, InputGroup } from '@/components/catalyst-ui/input'
import { Button } from '@/components/catalyst-ui/button'
import { Select } from '@/components/catalyst-ui/select'
import { Text } from '@/components/catalyst-ui/text'
import DomainTable from '@/features/domains/components/DomainTable'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useWorkspaceDomainsWithNs, useListUserDomains, useCheckUserDomainStatus, useCheckDomainWithNsStatus, useAddUserDomain, useConnectUserDomainToProject, useAddDomainWithNs, useDisconnectDomainWithNsFromProject, useDisconnectThirdPartyDomain } from '../hooks/useMyDomains'
import { showSimpleSuccess, showSimpleError } from '@/lib/toast.jsx'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { PinwheelLoader } from '@/components/ui/PinwheelLoader'
import { EmptyState } from '@/components/ui/EmptyState'
import { MagnifyingGlassIcon } from '@/assets/icons'
import { usePermissions } from '@/hooks/usePermissions'

export default function MyDomainsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('All Domains')
  const { workspaceId: activeWorkspaceId } = useActiveWorkspace()
  const { user } = useAuth()
  const { hasRole } = usePermissions()


  const workspaceIdFromUrl = searchParams.get('workspace')
  const cleanWorkspaceId = (id) => {
    if (!id) return null
    const match = id.match(/^([a-f0-9]{24})/i)
    return match ? match[1] : null
  }
  const workspaceId = cleanWorkspaceId(workspaceIdFromUrl) || cleanWorkspaceId(activeWorkspaceId)
  const isViewer = hasRole('viewer')

  useEffect(() => {
    const sessionId = searchParams.get('session_id')
    if (sessionId && workspaceId) {
      queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs', workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', user?.id, workspaceId] })
      showSimpleSuccess('Domain purchase completed successfully!')
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('session_id')
      if (workspaceId) {
        newParams.set('workspace', workspaceId)
      }
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams, queryClient, workspaceId, user?.id, setSearchParams])

  // Fetch user domainsNS
  const {
    data: workspaceDomainsData,
    isLoading: isLoadingDomains,
    isError: isDomainsError,
    error: domainsError
  } = useWorkspaceDomainsWithNs(workspaceId)


  //Fetch user domains third party
  const {
    data: userDomainsData,
    isLoading: isLoadingUserDomains,
    isError: isUserDomainsError,
    error: userDomainsError
  } = useListUserDomains(user?.id, {
    workspaceId,
    offset: 0,
    limit: 100 // Add a reasonable limit
  })

  // Validation hooks for both domain types
  const checkUserDomainStatus = useCheckUserDomainStatus()
  const checkDomainWithNsStatus = useCheckDomainWithNsStatus(false) // No toast in hook, handled in handler
  const addDomainWithNsMutation = useAddDomainWithNs()
  const addUserDomainMutation = useAddUserDomain()
  const connectUserDomainMutation = useConnectUserDomainToProject()
  const disconnectDomainWithNsMutation = useDisconnectDomainWithNsFromProject()
  const disconnectThirdPartyMutation = useDisconnectThirdPartyDomain()


  // Transform API data for DomainTable
  const domains = workspaceDomainsData?.data?.map((item) => ({
    id: item.id || item._id || item.domain_id,
    name: item.domain_name,
    projectName: null,
    projectId: item.project_id || null, // Use project_id from API
    project_id: item.project_id || null, // Keep original project_id field for withNS domains
    renewal: item.auto_renew ? 'Auto Renewal' : 'Disabled',
    expires: new Date(item.expiry_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }),
    status: item.status || (item.dnsVerified ? 'active' : 'pending'),
    dnsVerified: Boolean(item.dnsVerified), // Ensure boolean
    txtVerified: Boolean(item.txtVerified), // Ensure boolean
    domainType: 'withNS', // Mark as withNS domain
    rawDomain: item
  })) || [];
  // Transform third-party domains data
  const thirdPartyDomains = userDomainsData?.data?.map((item) => {

    // Get project ID - handle both string ID and populated object
    let projectId = null;
    if (item.project) {
      projectId = typeof item.project === 'string' ? item.project : item.project._id || item.project.id;
    }

    return {
      id: item._id || item.id,
      name: item.domain,
      connected: !!projectId, // Connected if has project_id
      projectName: null,
      projectId: projectId,
      project: projectId, // For thirdParty domains, use project ID
      renewal: 'Third Party',
      expires: '-', // Always show dash for third-party
      status: item.dnsVerified ? 'active' : 'pending',
      txtVerified: Boolean(item.txtVerified), // Ensure boolean
      dnsVerified: Boolean(item.dnsVerified), // Ensure boolean
      domainType: 'thirdParty', // Mark as third-party domain
      rawDomain: item
    };
  }) || [];

  const allDomains = [...domains, ...thirdPartyDomains]

  // Dynamic validation function based on domain type
  const handleValidateDomain = async (domain) => {
    try {
      if (domain.domainType === 'thirdParty') {
        // Use third-party validation
        const result = await checkUserDomainStatus.mutateAsync({
          userId: user?.id,
          domain: domain.name
        });

        if (result.message?.includes('successfully verified')) {
          showSimpleSuccess(`Domain ${domain.name} DNS verification successful`);
        } else {
          showSimpleError(`DNS verification failed for ${domain.name}. Please check your DNS settings.`);
        }
      } else if (domain.domainType === 'withNS') {
        // Use withNS validation
        const result = await checkDomainWithNsStatus.mutateAsync({
          workspaceId: workspaceId,
          domain: domain.name
        });

        if (result.message?.includes('successfully verified')) {
          showSimpleSuccess(`Domain ${domain.name} DNS verification successful`);
        } else {
          showSimpleError(`DNS verification failed for ${domain.name}. Please check your DNS settings.`);
        }
      }
    } catch (error) {
      console.error('DNS check failed:', error);
      showSimpleError(`DNS verification failed for ${domain.name}. Please try again.`);
    }
  }


  const handleDomainClick = (domainName) => {
    navigate(`/domains/my-domains/${domainName}?workspace=${workspaceId || ''}`)
  }

  const handleManage = (domain) => {
    // Navigate based on domain type
    if (domain.domainType === 'thirdParty') {
      // Navigate to DomainConfigPage for third-party domains
      navigate(`/domains/my-domains/config/${domain.name}?workspace=${workspaceId || ''}`)
    } else {
      // Navigate to domain details page for withNS domains
      navigate(`/domains/my-domains/${domain.name}?workspace=${workspaceId || ''}`)
    }
  }

  const handleRecordsClick = (domain) => {
    // Navigate based on domain type
    if (domain.domainType === 'thirdParty') {
      // Navigate to third-party records page
      navigate(`/domains/my-domain/records?domain=${domain.name}&workspace=${workspaceId || ''}`)
    } else {
      // Navigate to domain details page for withNS domains
      navigate(`/domains/my-domains/${domain.name}?workspace=${workspaceId || ''}`)
    }
  }

  // New function to handle disconnect for both withNS and third-party domains
  const handleDisconnectProjectFromNSDomain = async (domain) => {
    try {
      if (domain.domainType === 'withNS') {
        // Disconnect withNS domain
        await disconnectDomainWithNsMutation.mutateAsync({
          workspaceId: workspaceId,
          domain: domain.name,
        });
      } else if (domain.domainType === 'thirdParty') {
        // Disconnect third-party domain
        await disconnectThirdPartyMutation.mutateAsync({
          userId: user?.id,
          domain: domain.name,
        });
      }
    } catch (error) {
      console.error('Failed to disconnect domain:', error);
      showSimpleError(`Failed to disconnect ${domain.name}: ${error.message}`);
    }
  }

  const handleConnectProject = async (domain, project) => {
    if (project === null) {
      // Handle disconnect
      await handleDisconnectProjectFromNSDomain(domain);
      return;
    }

    try {
      if (domain.domainType === 'thirdParty') {
        // For third-party domains, always use the connectUserDomainToProject mutation
        await connectUserDomainMutation.mutateAsync({
          userId: user?.id,
          domain: domain.name,
          project: project.uniqueUrl || project.name
        });
        // Invalidate domain details so verification status is refetched
        queryClient.invalidateQueries(['thirdPartyDomainDetails'])
        // Success dialog will be shown by DomainTable component
      } else {
        // For withNS domains, use POST to add domain with project and TXT records
        await addDomainWithNsMutation.mutateAsync({
          domainData: {
            name: domain.name,
            expiry_date: domain.expiry_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
          project: project,
          workspaceId: workspaceId
        });
        // Success dialog will be shown by DomainTable component
      }
    } catch (error) {
      console.error('Failed to connect domain to project:', error);
      showSimpleError(`Failed to connect ${domain.name} to project: ${error.message}`);
    }
  }

  // Filter domains based on search and filter type
  const filteredDomains = allDomains.filter(domain => {
    const matchesSearch = domain.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterType === 'All Domains' ||
      (filterType === 'Connected' && domain.connected) ||
      (filterType === 'Not Connected' && !domain.connected)

    return matchesSearch && matchesFilter
  });

  // Loading state
  if (isLoadingDomains || isLoadingUserDomains) {
    return (
      <div className="flex items-center justify-center py-12">
        <PinwheelLoader text="Loading your domains..." />
      </div>
    );
  }

  // Error state
  if (isDomainsError || isUserDomainsError) {
    return (
      <div className="max-w-[1200px] mx-auto space-y-6 p-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-900">My Domains</h1>
        </div>
        <div className="text-center py-12">
          <Text className="!text-sm !font-medium !text-[#71717A] mb-4">
            Error loading domains: {domainsError?.message || userDomainsError?.message}
          </Text>
          <Button onClick={() => window.location.reload()} outline className="data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen mx-auto space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">My Domains</h1>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="flex-1 w-full sm:max-w-sm">
          <InputGroup>
            <MagnifyingGlassIcon data-slot="icon" />
            <Input
              type="text"
              placeholder="Search Domain"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </InputGroup>
        </div>

        <div className="w-full sm:w-auto">
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full sm:w-fit"
          >
            <option value="All Domains">All Domains</option>
            <option value="Connected">Connected</option>
            <option value="Not Connected">Not Connected</option>
          </Select>
        </div>

        {!isViewer && (
          <Button
            to={`/domains/buy-domains?workspace=${workspaceId}`}
            color="dark"
            className="w-full sm:w-auto ml-auto data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            Buy or Add New Domain
          </Button>
        )}
      </div>

      {/* Domains Table or Empty State */}
      {filteredDomains.length > 0 ? (
        <DomainTable
          domains={filteredDomains}
          onDomainClick={handleDomainClick}
          onManage={handleManage}
          onConnect={handleConnectProject}
          onDisconnect={handleDisconnectProjectFromNSDomain}
          onValidate={handleValidateDomain}
          onRecordsClick={handleRecordsClick}
          onDelete={(domain) => {
            // Invalidate queries after domain deletion to refresh the list
            queryClient.invalidateQueries({ queryKey: ['workspaceDomainsWithNs', workspaceId] });
            queryClient.invalidateQueries({ queryKey: ['userDomainsByScope', user?.id, workspaceId] });
          }}
        />
      ) : (
        <EmptyState
          title="No domains"
          description="Get started by adding or buying a domain."
          showButton={!isViewer}
          buttonLabel="Buy or Add New Domain"
          onButtonClick={() => navigate(`/domains/buy-domains?workspace=${workspaceId}`)}
          className="-mt-2"
        />
      )}
    </div>
  )
}
